import { useCallback, useState } from 'react';
import { searchImages } from '../utils/imageSearch';
import { useThemeStore } from '../store/themeStore';
import { Theme } from '../store/types/theme';

type AnimeThemeSelectorState = {
    loadingId: string | null;
    error: string | null;
    previewId: string | null;
    candidates: string[];
    candidateIndex: number;
    fetchImage: (theme: Theme) => Promise<void>;
    nextCandidate: (theme: Theme) => Promise<void>;
    clearImage: (theme: Theme) => void;
    openPreview: (theme: Theme) => void;
    closePreview: () => void;
    pickCandidate: (theme: Theme, url: string) => void;
};

export const useAnimeThemeImages = (): AnimeThemeSelectorState => {
    const { updateTheme } = useThemeStore();
    const [loadingId, setLoadingId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [previewId, setPreviewId] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<string[]>([]);
    const [candidateIndex, setCandidateIndex] = useState(0);

    const fetchImage = useCallback(
        async (theme: Theme) => {
            if (!theme.bgQuery) return;
            setLoadingId(theme.id);
            setError(null);
            try {
                const urls = await searchImages(theme.bgQuery, 12);
                setCandidates(urls);
                setCandidateIndex(0);
                if (urls[0]) {
                    updateTheme(theme.id, { bgImage: urls[0] });
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Image search failed');
            } finally {
                setLoadingId(null);
            }
        },
        [updateTheme]
    );

    const nextCandidate = useCallback(
        async (theme: Theme) => {
            // If we already have candidates, cycle through them; otherwise fetch.
            if (candidates.length > 0) {
                const nextIdx = (candidateIndex + 1) % candidates.length;
                setCandidateIndex(nextIdx);
                updateTheme(theme.id, { bgImage: candidates[nextIdx] });
                return;
            }
            await fetchImage(theme);
        },
        [candidates, candidateIndex, fetchImage, updateTheme]
    );

    const clearImage = useCallback(
        (theme: Theme) => {
            updateTheme(theme.id, { bgImage: undefined });
        },
        [updateTheme]
    );

    const openPreview = useCallback((theme: Theme) => {
        setPreviewId(theme.id);
    }, []);

    const closePreview = useCallback(() => {
        setPreviewId(null);
        setCandidates([]);
        setCandidateIndex(0);
    }, []);

    const pickCandidate = useCallback(
        (theme: Theme, url: string) => {
            updateTheme(theme.id, { bgImage: url });
            closePreview();
        },
        [updateTheme, closePreview]
    );

    return {
        loadingId,
        error,
        previewId,
        candidates,
        candidateIndex,
        fetchImage,
        nextCandidate,
        clearImage,
        openPreview,
        closePreview,
        pickCandidate,
    };
};
